import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Divider,
  Chip,
  ListItemButton,
  ListItemContent,
  Drawer,
  List,
  ListItemDecorator,
  ModalClose,
} from '@mui/joy';
import Collapse from '@mui/material/Collapse';
import {
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  Settings as SettingsIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  LocalShipping as LocalShippingIcon,
  AddCircle as AddCircleIcon,
  QrCodeScanner as QrCodeScannerIcon,
  FileUpload as FileUploadIcon,
  AttachMoney as AttachMoneyIcon,
  Rule as RuleIcon,
  Receipt as ReceiptIcon,
  Group as GroupIcon,
  Payment as PaymentIcon,
  Phone as PhoneIcon2,
  WhatsApp as WhatsAppIcon,
  SmartToy as SmartToyIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  LocationOn as LocationOnIcon,
  AccountBalance as AccountBalanceIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Timeline as TimelineIcon,
  ListAlt as ListAltIcon,
  Warehouse as WarehouseIcon,
  HowToReg as HowToRegIcon,
} from '@mui/icons-material';
import { useAuth, PERMISSIONS } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { getSiteDisplayName } from '../utils/siteDisplayName';
import { useNavigate, useLocation } from 'react-router-dom';
import PermissionGate from './PermissionGate';
import { useTranslation } from 'react-i18next';
import { setAppLanguage, resolveAppLanguage, isRtlLocale } from '../../../i18n';
import Button from '@mui/joy/Button';
import { getLocalizedUserFullName, getLocalizedUserInitials } from '../utils/userDisplayName';

/** Settings → permissions / roles / users / user-roles: one *_list per route (see routePermissions). */
const SETTINGS_RBAC_OPEN_PAGE_PERMISSIONS = [
  PERMISSIONS.PERMISSIONS_LIST,
  PERMISSIONS.ROLES_LIST,
  PERMISSIONS.USERS_LIST,
  PERMISSIONS.USER_ROLES_LIST,
];

/** Settings → Master: open page = matching *_list codename per route (see routePermissions). */
const MASTER_SETTINGS_OPEN_PAGE_PERMISSIONS = [
  PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST,
  PERMISSIONS.SHIPPING_COMPANIES_LIST,
  PERMISSIONS.SHIPPING_WAREHOUSES_LIST,
  PERMISSIONS.SHIPMENT_TYPES_LIST,
  PERMISSIONS.LOCATIONS_LIST,
  PERMISSIONS.ACCOUNTS_LIST,
  PERMISSIONS.SETTLEMENT_GROUPS_LIST,
];

/** Top-level Shipments accordion — any shipment submenu permission. */
const SHIPMENT_NAV_PERMISSIONS = [
  PERMISSIONS.SHIPMENTS_LIST,
  PERMISSIONS.SHIPMENTS_CREATE,
  PERMISSIONS.SHIPMENTS_VIEW,
  PERMISSIONS.SHIPMENTS_UPLOAD_EXCEL,
  PERMISSIONS.SHIPMENTS_SCAN_UPDATE,
  PERMISSIONS.TRACKING_EVENTS_LIST,
  PERMISSIONS.SHIPMENT_UNITS_LIST,
  PERMISSIONS.SHIPMENT_UNIT_TRACKING_EVENTS_LIST,
  PERMISSIONS.RECEIVER_DETAILS_LIST,
  PERMISSIONS.SHIPMENT_PIECES_LIST,
  PERMISSIONS.SHIPMENT_PIECES_SCAN_UPDATE,
  PERMISSIONS.SHIPMENT_PIECE_TRACKING_EVENTS_LIST,
  PERMISSIONS.SCAN_RESOLVE,
];

const SHIPMENT_OPERATIONS_PERMISSIONS = [
  PERMISSIONS.SHIPMENTS_LIST,
  PERMISSIONS.SHIPMENTS_CREATE,
  PERMISSIONS.SHIPMENTS_UPLOAD_EXCEL,
];

const SHIPMENT_SCANNING_PERMISSIONS = [
  PERMISSIONS.SHIPMENT_PIECES_SCAN_UPDATE,
  PERMISSIONS.SHIPMENTS_SCAN_UPDATE,
  PERMISSIONS.SCAN_RESOLVE,
];

const SHIPMENT_TRACKING_PERMISSIONS = [
  PERMISSIONS.SHIPMENT_PIECE_TRACKING_EVENTS_LIST,
  PERMISSIONS.TRACKING_EVENTS_LIST,
  PERMISSIONS.SHIPMENT_UNIT_TRACKING_EVENTS_LIST,
];

const SHIPMENT_INVENTORY_PERMISSIONS = [
  PERMISSIONS.SHIPMENT_PIECES_LIST,
  PERMISSIONS.RECEIVER_DETAILS_LIST,
  PERMISSIONS.SHIPMENT_UNITS_LIST,
];

const FINANCE_NAV_PERMISSIONS = [
  PERMISSIONS.PRICING_PLANS_LIST,
  PERMISSIONS.PRICING_RULES_LIST,
  PERMISSIONS.INVOICES_LIST,
  PERMISSIONS.PAYMENTS_LIST,
];

const CUSTOMERS_NAV_PERMISSIONS = [
  PERMISSIONS.CUSTOMERS_LIST,
  PERMISSIONS.CUSTOMERS_APPROVE,
];

const SETTINGS_NAV_PERMISSIONS = [
  PERMISSIONS.SETTINGS_VIEW_PUBLIC,
  PERMISSIONS.SETTINGS_LIST,
  PERMISSIONS.SETTINGS_UPDATE_SITE,
  PERMISSIONS.SETTINGS_VIEW_DETAIL,
  PERMISSIONS.SETTINGS_UPDATE_DETAIL,
  PERMISSIONS.SETTINGS_FILES_LIST,
  PERMISSIONS.SETTINGS_FILES_UPLOAD,
  ...SETTINGS_RBAC_OPEN_PAGE_PERMISSIONS,
  ...MASTER_SETTINGS_OPEN_PAGE_PERMISSIONS,
  PERMISSIONS.LOCATIONS_MANAGE,
];

const SETTINGS_SITE_PERMISSIONS = [PERMISSIONS.SETTINGS_LIST];
const SETTINGS_INTEGRATIONS_PERMISSIONS = [PERMISSIONS.SETTINGS_LIST];
const SETTINGS_ACCESS_PERMISSIONS = [...SETTINGS_RBAC_OPEN_PAGE_PERMISSIONS];

/** Compute { show, separated } for each section; separated only when a prior section is visible. */
function navSectionFlags(visibilityList) {
  let seenVisible = false;
  return visibilityList.map((show) => {
    const separated = Boolean(show && seenVisible);
    if (show) seenVisible = true;
    return { show: Boolean(show), separated };
  });
}

function NavSectionLabel({ label, nested = false, separated = false }) {
  return (
    <Box
      sx={{
        px: nested ? 1.5 : 1,
        pt: separated ? (nested ? 1.25 : 1.75) : nested ? 1.25 : 1.5,
        pb: 0.5,
        ...(separated && {
          mt: nested ? 1.25 : 1.75,
          borderTop: '1px solid #e5e7eb',
        }),
      }}
    >
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: '#9ca3af',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function AppBar() {
  const { t, i18n } = useTranslation('ui');
  const { settings } = useSiteSettings();
  const siteTitle = getSiteDisplayName(settings, i18n.language);
  const { user, logout, getAllUserRoles, hasPermission, hasAnyPermission } = useAuth();
  const currentLang = resolveAppLanguage(i18n.language);
  const drawerAnchor = isRtlLocale(i18n.language) ? 'right' : 'left';
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [shipmentOpen, setShipmentOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [masterOpen, setMasterOpen] = useState(false);
  const [customersOpen, setCustomersOpen] = useState(true);

  const shipmentSectionRef = useRef(null);
  const settingsSectionRef = useRef(null);
  const masterSectionRef = useRef(null);
  const customersSectionRef = useRef(null);
  const collapsePrevRef = useRef({
    shipment: true,
    settings: false,
    master: false,
    customers: true,
  });

  /** After accordion expands, scroll the section into view inside the drawer (smooth, after Collapse animation). */
  useEffect(() => {
    if (!drawerOpen) {
      collapsePrevRef.current = {
        shipment: shipmentOpen,
        settings: settingsOpen,
        master: masterOpen,
        customers: customersOpen,
      };
      return undefined;
    }
    const prev = collapsePrevRef.current;
    const timeouts = [];

    const scheduleScroll = (el) => {
      if (!el) return;
      const t = window.setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }, 320);
      timeouts.push(t);
    };

    if (shipmentOpen && !prev.shipment && shipmentSectionRef.current) {
      scheduleScroll(shipmentSectionRef.current);
    }
    if (settingsOpen && !prev.settings && settingsSectionRef.current) {
      scheduleScroll(settingsSectionRef.current);
    }
    if (masterOpen && !prev.master && masterSectionRef.current) {
      scheduleScroll(masterSectionRef.current);
    }
    if (customersOpen && !prev.customers && customersSectionRef.current) {
      scheduleScroll(customersSectionRef.current);
    }

    collapsePrevRef.current = {
      shipment: shipmentOpen,
      settings: settingsOpen,
      master: masterOpen,
      customers: customersOpen,
    };

    return () => {
      timeouts.forEach((id) => window.clearTimeout(id));
    };
  }, [drawerOpen, shipmentOpen, settingsOpen, masterOpen, customersOpen]);

  // Get user data with fallback to localStorage
  const getUserData = () => {
    if (user) return user;
    
    const storedUserData = localStorage.getItem('user_data');
    if (storedUserData) {
      try {
        return JSON.parse(storedUserData);
      } catch (e) {
        console.error('Error parsing stored user data:', e);
      }
    }
    return null;
  };

  const userData = getUserData();
  const displayName =
    getLocalizedUserFullName(userData, i18n.language) || (userData && userData.username) || t('actions.user');
  const userRoles = getAllUserRoles();

  // Function to check if a route is active
  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isMasterSettingsActive =
    isActiveRoute('/settings/tracking-event-types') ||
    isActiveRoute('/settings/shipping-companies') ||
    isActiveRoute('/settings/shipping-warehouses') ||
    isActiveRoute('/settings/shipment-types') ||
    isActiveRoute('/settings/locations') ||
    isActiveRoute('/settings/accounts') ||
    isActiveRoute('/settings/settlementgroups');

  const customerListAllowed = hasPermission(PERMISSIONS.CUSTOMERS_LIST);
  const customerApproveAllowed = hasPermission(PERMISSIONS.CUSTOMERS_APPROVE);
  const showCustomersAccordion = customerListAllowed && customerApproveAllowed;
  const isCustomersNavSectionActive =
    isActiveRoute('/Customers') || isActiveRoute('/customers/pending-registrations');

  const canSeeNavSection = (permissions) => hasAnyPermission(permissions);

  const showOverviewSection = true;
  const showShipmentsSection = canSeeNavSection(SHIPMENT_NAV_PERMISSIONS);
  const showFinanceSection = canSeeNavSection(FINANCE_NAV_PERMISSIONS);
  const showCustomersSection = canSeeNavSection(CUSTOMERS_NAV_PERMISSIONS);
  const showSettingsSection = canSeeNavSection(SETTINGS_NAV_PERMISSIONS);

  const [
    overviewNavSection,
    shipmentsNavSection,
    financeNavSection,
    customersNavSection,
    settingsNavSection,
  ] = navSectionFlags([
    showOverviewSection,
    showShipmentsSection,
    showFinanceSection,
    showCustomersSection,
    showSettingsSection,
  ]);

  const [
    shipmentOperationsNavSection,
    shipmentScanningNavSection,
    shipmentTrackingNavSection,
    shipmentInventoryNavSection,
  ] = navSectionFlags([
    canSeeNavSection(SHIPMENT_OPERATIONS_PERMISSIONS),
    canSeeNavSection(SHIPMENT_SCANNING_PERMISSIONS),
    canSeeNavSection(SHIPMENT_TRACKING_PERMISSIONS),
    canSeeNavSection(SHIPMENT_INVENTORY_PERMISSIONS),
  ]);

  const [
    settingsSiteNavSection,
    settingsIntegrationsNavSection,
    settingsAccessNavSection,
    settingsMasterNavSection,
  ] = navSectionFlags([
    canSeeNavSection(SETTINGS_SITE_PERMISSIONS),
    canSeeNavSection(SETTINGS_INTEGRATIONS_PERMISSIONS),
    canSeeNavSection(SETTINGS_ACCESS_PERMISSIONS),
    canSeeNavSection(MASTER_SETTINGS_OPEN_PAGE_PERMISSIONS),
  ]);

  const handleUserMenuOpen = () => {
    setUserMenuOpen(true);
  };

  const handleUserMenuClose = () => {
    setUserMenuOpen(false);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  const getInitials = () => getLocalizedUserInitials(userData, i18n.language);

  return (
    <>
      {/* AppBar */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 64,
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          zIndex: 1200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingX: 3,
          paddingY: 0,
        }}
      >
        {/* Left side - Drawer Button */}
        <IconButton
          variant="plain"
          onClick={() => setDrawerOpen(true)}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(229, 231, 235, 0.5)',
            borderRadius: '12px',
            width: 44,
            height: 44,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(249, 250, 251, 0.9)',
              borderColor: 'rgba(209, 213, 219, 0.6)',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
              transform: 'scale(1.02)',
            },
            '&:active': {
              transform: 'scale(0.98)',
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <MenuIcon 
            sx={{ 
              color: '#667eea',
              fontSize: 22,
              transition: 'transform 0.2s ease',
            }} 
          />
        </IconButton>

        {/* Center - site name from settings */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          <Typography
            level="title-md"
            sx={{
              fontWeight: 700,
              color: '#111827',
              letterSpacing: '-0.02em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              px: 1,
            }}
          >
            {siteTitle}
          </Typography>
        </Box>

        {/* Right side - Language + User Profile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            role="group"
            aria-label={t('language.languageToggle')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              p: 0.25,
              borderRadius: '10px',
              border: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
            }}
          >
            <Button
              size="sm"
              variant={currentLang === 'en' ? 'solid' : 'plain'}
              color={currentLang === 'en' ? 'primary' : 'neutral'}
              onClick={() => setAppLanguage('en')}
              sx={{ minWidth: 40, fontWeight: 600, fontSize: '12px', py: 0.25, px: 0.75 }}
            >
              {t('language.english')}
            </Button>
            <Button
              size="sm"
              variant={currentLang === 'zh' ? 'solid' : 'plain'}
              color={currentLang === 'zh' ? 'primary' : 'neutral'}
              onClick={() => setAppLanguage('zh')}
              sx={{ minWidth: 40, fontWeight: 600, fontSize: '12px', py: 0.25, px: 0.75 }}
            >
              {t('language.chinese')}
            </Button>
            <Button
              size="sm"
              variant={currentLang === 'ar' ? 'solid' : 'plain'}
              color={currentLang === 'ar' ? 'primary' : 'neutral'}
              onClick={() => setAppLanguage('ar')}
              sx={{ minWidth: 40, fontWeight: 600, fontSize: '12px', py: 0.25, px: 0.75 }}
            >
              {t('language.arabic')}
            </Button>
          </Box>
          {/* User menu: anchor dropdown to trigger only so RTL/LTR alignment matches viewport edge */}
          <Box sx={{ position: 'relative' }}>
          {/* User Info */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              paddingX: 1.5,
              paddingY: 0.75,
              borderRadius: '10px',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              border: '1px solid transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: '#f9fafb',
                borderColor: '#e5e7eb',
              },
            }}
            onClick={handleUserMenuOpen}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: '#f3f4f6',
                border: '1px solid #e5e7eb',
                fontSize: '14px',
                fontWeight: 600,
                color: '#667eea',
                boxShadow: 'none',
              }}
            >
              {getInitials()}
            </Avatar>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography 
                level="body-sm" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#111827',
                  fontSize: '14px',
                  lineHeight: 1.2,
                }}
              >
                {displayName}
              </Typography>
              {userRoles && userRoles.length > 0 && (
                <Typography 
                  level="body-xs" 
                  sx={{ 
                    color: '#6b7280', 
                    fontSize: '11px',
                    lineHeight: 1.2,
                    mt: 0.25,
                  }}
                >
                  {userRoles[0].replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Typography>
              )}
            </Box>
            <ArrowDropDownIcon 
              sx={{ 
                fontSize: 20, 
                color: '#6b7280',
                transition: 'transform 0.2s ease',
                transform: userMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }} 
            />
          </Box>

          {/* User Menu Dropdown */}
          {userMenuOpen && (
            <>
              {/* Backdrop */}
              <Box
                onClick={handleUserMenuClose}
                sx={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1299,
                }}
              />
              {/* Dropdown Menu */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  insetInlineEnd: 0,
                  mt: 1,
                  width: { xs: 'min(90vw, 400px)', sm: 400 },
                  maxWidth: 'min(400px, calc(100vw - 24px))',
                  maxHeight: '85vh',
                  overflow: 'auto',
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                  border: '1px solid #e5e7eb',
                  zIndex: 1300,
                  animation: 'slideDown 0.2s ease-out',
                  '@keyframes slideDown': {
                    from: {
                      opacity: 0,
                      transform: 'translateY(-10px)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
              
              {/* Profile Header */}
              <Box 
                sx={{ 
                  p: 3, 
                  backgroundColor: '#f9fafb',
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      backgroundColor: '#f3f4f6',
                      border: '2px solid #e5e7eb',
                      fontSize: '20px',
                      fontWeight: 600,
                      color: '#667eea',
                      boxShadow: 'none',
                    }}
                  >
                    {getInitials()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      level="title-md" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#111827', 
                        mb: 0.5,
                        fontSize: '16px',
                      }}
                    >
                      {displayName}
                    </Typography>
                    {userData?.email && (
                      <Typography 
                        level="body-sm" 
                        sx={{ 
                          color: '#6b7280', 
                          fontSize: '13px',
                        }}
                      >
                        {userData.email}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Roles */}
                {userRoles && userRoles.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {userRoles.map((role, index) => (
                      <Chip
                        key={index}
                        size="sm"
                        variant="soft"
                        sx={{ 
                          fontSize: '11px',
                          backgroundColor: '#ede9fe',
                          color: '#667eea',
                          border: 'none',
                          fontWeight: 500,
                        }}
                      >
                        {role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Chip>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Account Details */}
              <Box sx={{ p: 3, backgroundColor: '#ffffff' }}>
                <Typography
                  level="body-xs"
                  sx={{
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 2,
                    fontSize: '11px',
                  }}
                >
                  {t('userMenu.accountInformation')}
                </Typography>

                {/* User ID */}
                {userData?.id && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5, 
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #f3f4f6',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                        borderColor: '#e5e7eb',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: '#fef3c7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BadgeIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-xs" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 500, mb: 0.5 }}>
                        {t('userMenu.userId')}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: '#111827', fontWeight: 500, fontSize: '14px' }}>
                        {userData.id}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Username */}
                {userData?.username && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5, 
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #f3f4f6',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                        borderColor: '#e5e7eb',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: '#e0e7ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 18, color: '#667eea' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-xs" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 500, mb: 0.5 }}>
                        {t('userMenu.username')}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: '#111827', fontWeight: 500, fontSize: '14px' }}>
                        {userData.username}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Email */}
                {userData?.email && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5, 
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #f3f4f6',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                        borderColor: '#e5e7eb',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: '#d1fae5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <EmailIcon sx={{ fontSize: 18, color: '#10b981' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-xs" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 500, mb: 0.5 }}>
                        {t('userMenu.email')}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: '#111827', fontWeight: 500, fontSize: '14px' }}>
                        {userData.email}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Phone */}
                {userData?.phone_number && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5, 
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #f3f4f6',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                        borderColor: '#e5e7eb',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: '#fce7f3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PhoneIcon sx={{ fontSize: 18, color: '#ec4899' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-xs" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 500, mb: 0.5 }}>
                        {t('userMenu.phone')}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: '#111827', fontWeight: 500, fontSize: '14px' }}>
                        {userData.phone_number}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Full Name */}
                {(userData?.first_name ||
                  userData?.last_name ||
                  userData?.first_name_ar ||
                  userData?.last_name_ar) && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5, 
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #f3f4f6',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                        borderColor: '#e5e7eb',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: '#fed7aa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BadgeIcon sx={{ fontSize: 18, color: '#f97316' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-xs" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 500, mb: 0.5 }}>
                        {t('userMenu.fullName')}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: '#111827', fontWeight: 500, fontSize: '14px' }}>
                        {getLocalizedUserFullName(userData, i18n.language) || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Branch */}
                {userData?.branch && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #f3f4f6',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                        borderColor: '#e5e7eb',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: '#d1fae5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BusinessIcon sx={{ fontSize: 18, color: '#10b981' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-xs" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 500, mb: 0.5 }}>
                        {t('userMenu.branch')}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: '#111827', fontWeight: 500, fontSize: '14px' }}>
                        {userData.branch}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 0 }} />

              {/* Actions */}
              <Box sx={{ p: 2, backgroundColor: '#ffffff', borderTop: '1px solid #e5e7eb' }}>
                <ListItemButton
                  onClick={handleLogout}
                  sx={{
                    borderRadius: '8px',
                    p: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexDirection: 'row',
                    backgroundColor: 'transparent',
                    color: '#dc2626',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: '#fef2f2',
                    },
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 20, flexShrink: 0, transition: 'color 0.2s ease' }} />
                  <ListItemContent>
                    <Typography level="body-sm" sx={{ fontWeight: 500, fontSize: '14px' }}>
                      {t('actions.logout')}
                    </Typography>
                  </ListItemContent>
                </ListItemButton>
              </Box>
              </Box>
            </>
          )}
          </Box>
        </Box>
      </Box>

      {/* Navigation Drawer */}
      <Drawer
        anchor={drawerAnchor}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-content': {
            width: 320,
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            maxHeight: '100vh',
            overflow: 'hidden',
            background: '#ffffff',
            color: '#333',
            ...(drawerAnchor === 'left'
              ? { borderRight: '1px solid rgba(229, 231, 235, 0.5)' }
              : { borderLeft: '1px solid rgba(229, 231, 235, 0.5)' }),
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
          },
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #f8f9fa 0%, #f1f3f5 100%)',
            padding: 3,
            color: '#111827',
            borderBottom: '1px solid rgba(229, 231, 235, 0.6)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ 
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                borderRadius: '12px', 
                padding: 1.2,
                marginRight: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <MenuIcon sx={{ fontSize: 22, color: '#667eea' }} />
              </Box>
              <Typography fontSize="xl" fontWeight="600" sx={{ color: '#111827', letterSpacing: '0.2px' }}>
                {t('nav.mainMenu')}
              </Typography>
            </Box>
            <ModalClose
              onClick={() => setDrawerOpen(false)}
              sx={{ 
                color: '#6b7280',
                backgroundColor: 'rgba(243, 244, 246, 0.8)',
                borderRadius: '10px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(229, 231, 235, 0.8)',
                  transform: 'scale(1.05)',
                }
              }}
            />
          </Box>
        </Box>

          {/* Navigation Items */}
        <Box
          sx={{ 
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          scrollBehavior: 'smooth',
          backgroundColor: '#ffffff',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#e5e7eb',
            borderRadius: '3px',
            '&:hover': {
              background: '#d1d5db',
            },
          },
        }}>
          <List
            size="lg"
            component="nav"
            sx={{
              padding: 2,
              '& .MuiListItemButton-root': {
                borderRadius: '10px',
                margin: '3px 0',
                padding: '11px 13px',
                transition: 'all 0.2s ease',
                border: '1px solid transparent',
                position: 'relative',
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.06)',
                  transform: 'translateX(3px)',
                  border: '1px solid rgba(102, 126, 234, 0.15)',
                },
                '&:active': {
                  transform: 'translateX(1px)',
                },
              },
              '& .MuiListItemDecorator-root': {
                minWidth: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
            }}
          >
          {/* Home & Dashboard — Overview */}
          {overviewNavSection.show && (
            <NavSectionLabel
              label={t('nav.sectionOverview')}
              separated={overviewNavSection.separated}
            />
          )}
          {/* Home */}
          <ListItemButton 
            onClick={() => { setDrawerOpen(false); navigate("/home"); }}
            sx={{
              backgroundColor: isActiveRoute('/home') 
                ? 'rgba(102, 126, 234, 0.08)' 
                : 'transparent',
              border: isActiveRoute('/home') 
                ? '1px solid rgba(102, 126, 234, 0.2)' 
                : '1px solid transparent',
              position: 'relative',
              '&::before': isActiveRoute('/home') ? {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: '3px',
                height: '50%',
                backgroundColor: '#667eea',
                borderRadius: '0 3px 3px 0',
              } : {},
              '&:hover': {
                backgroundColor: isActiveRoute('/home') 
                  ? 'rgba(102, 126, 234, 0.1)' 
                  : 'rgba(102, 126, 234, 0.06)',
                border: isActiveRoute('/home') 
                  ? '1px solid rgba(102, 126, 234, 0.25)' 
                  : '1px solid rgba(102, 126, 234, 0.15)',
              }
            }}
          >
            <ListItemDecorator>
              <Box sx={{ 
                backgroundColor: isActiveRoute('/home') 
                  ? 'rgba(102, 126, 234, 0.12)'
                  : 'rgba(243, 244, 246, 0.8)',
                borderRadius: '10px', 
                padding: 0.9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}>
                <HomeIcon sx={{ 
                  fontSize: 20, 
                  color: isActiveRoute('/home') ? '#667eea' : '#6b7280',
                  transition: 'all 0.2s ease',
                }} />
              </Box>
            </ListItemDecorator>
            <Typography sx={{ 
              fontWeight: isActiveRoute('/home') ? '600' : '500',
              fontSize: '15px',
              color: isActiveRoute('/home') ? '#667eea' : '#374151',
              letterSpacing: '0.1px',
            }}>
              {t('nav.home')}
            </Typography>
          </ListItemButton>

          {/* Dashboard */}
          <PermissionGate permissions={[PERMISSIONS.DASHBOARD_VIEW_ANALYTICS]}>
            <ListItemButton 
              onClick={() => { setDrawerOpen(false); navigate("/dashboard"); }}
              sx={{
                backgroundColor: isActiveRoute('/dashboard') 
                  ? 'rgba(102, 126, 234, 0.08)' 
                  : 'transparent',
                border: isActiveRoute('/dashboard') 
                  ? '1px solid rgba(102, 126, 234, 0.2)' 
                  : '1px solid transparent',
                position: 'relative',
                '&::before': isActiveRoute('/dashboard') ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '3px',
                  height: '50%',
                  backgroundColor: '#667eea',
                  borderRadius: '0 3px 3px 0',
                } : {},
                '&:hover': {
                  backgroundColor: isActiveRoute('/dashboard') 
                    ? 'rgba(102, 126, 234, 0.1)' 
                    : 'rgba(102, 126, 234, 0.06)',
                  border: isActiveRoute('/dashboard') 
                    ? '1px solid rgba(102, 126, 234, 0.25)' 
                    : '1px solid rgba(102, 126, 234, 0.15)',
                }
              }}
            >
              <ListItemDecorator>
                <Box sx={{ 
                  backgroundColor: isActiveRoute('/dashboard') 
                    ? 'rgba(102, 126, 234, 0.12)'
                    : 'rgba(243, 244, 246, 0.8)',
                  borderRadius: '10px', 
                  padding: 0.9,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}>
                  <DashboardIcon sx={{ 
                    fontSize: 20, 
                    color: isActiveRoute('/dashboard') ? '#667eea' : '#6b7280',
                    transition: 'all 0.2s ease',
                  }} />
                </Box>
              </ListItemDecorator>
              <Typography sx={{ 
                fontWeight: isActiveRoute('/dashboard') ? '600' : '500',
                fontSize: '15px',
                color: isActiveRoute('/dashboard') ? '#667eea' : '#374151',
                letterSpacing: '0.1px',
              }}>
                {t('nav.dashboard')}
              </Typography>
            </ListItemButton>
          </PermissionGate>

          {shipmentsNavSection.show && (
            <NavSectionLabel
              label={t('nav.sectionShipments')}
              separated={shipmentsNavSection.separated}
            />
          )}
          {/* Shipments */}
          <PermissionGate permissions={SHIPMENT_NAV_PERMISSIONS}>
            <Box ref={shipmentSectionRef}>
            <ListItemButton 
              onClick={() => setShipmentOpen(!shipmentOpen)}
              sx={{
                backgroundColor: (isActiveRoute('/Shipments') || isActiveRoute('/shipment')) 
                  ? 'rgba(102, 126, 234, 0.08)' 
                  : 'transparent',
                border: (isActiveRoute('/Shipments') || isActiveRoute('/shipment')) 
                  ? '1px solid rgba(102, 126, 234, 0.2)' 
                  : '1px solid transparent',
                position: 'relative',
                '&::before': (isActiveRoute('/Shipments') || isActiveRoute('/shipment')) ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '3px',
                  height: '50%',
                  backgroundColor: '#667eea',
                  borderRadius: '0 3px 3px 0',
                } : {},
                '&:hover': {
                  backgroundColor: (isActiveRoute('/Shipments') || isActiveRoute('/shipment')) 
                    ? 'rgba(102, 126, 234, 0.1)' 
                    : 'rgba(102, 126, 234, 0.06)',
                  border: (isActiveRoute('/Shipments') || isActiveRoute('/shipment')) 
                    ? '1px solid rgba(102, 126, 234, 0.25)' 
                    : '1px solid rgba(102, 126, 234, 0.15)',
                }
              }}
            >
              <ListItemDecorator>
                <Box sx={{ 
                  backgroundColor: (isActiveRoute('/Shipments') || isActiveRoute('/shipment')) 
                    ? 'rgba(102, 126, 234, 0.12)'
                    : 'rgba(243, 244, 246, 0.8)',
                  borderRadius: '10px', 
                  padding: 0.9,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}>
                  <LocalShippingIcon sx={{ 
                    fontSize: 20, 
                    color: (isActiveRoute('/Shipments') || isActiveRoute('/shipment')) ? '#667eea' : '#6b7280',
                    transition: 'all 0.2s ease',
                  }} />
                </Box>
              </ListItemDecorator>
              <Typography sx={{ 
                fontWeight: (isActiveRoute('/Shipments') || isActiveRoute('/shipment')) ? '600' : '500',
                fontSize: '15px',
                color: (isActiveRoute('/Shipments') || isActiveRoute('/shipment')) ? '#667eea' : '#374151',
                letterSpacing: '0.1px',
                flexGrow: 1 
              }}>
                {t('nav.shipments')}
              </Typography>
              {shipmentOpen ? (
                <ExpandLessIcon sx={{ color: '#667eea', fontSize: 20, transition: 'transform 0.2s ease' }} />
              ) : (
                <ExpandMoreIcon sx={{ color: '#6b7280', fontSize: 20, transition: 'transform 0.2s ease' }} />
              )}
            </ListItemButton>
            <Collapse in={shipmentOpen} timeout="auto" unmountOnExit>
              <List sx={{ pl: 3, padding: '4px 8px', mt: 0.5 }}>
                {shipmentOperationsNavSection.show && (
                  <NavSectionLabel
                    label={t('nav.sectionOperations')}
                    nested
                    separated={shipmentOperationsNavSection.separated}
                  />
                )}
                <PermissionGate permissions={[PERMISSIONS.SHIPMENTS_LIST]}>
                <ListItemButton 
                  onClick={() => { setDrawerOpen(false); navigate("/Shipments"); }}
                  sx={{
                    backgroundColor: isActiveRoute('/Shipments') 
                      ? 'rgba(102, 126, 234, 0.08)' 
                      : 'transparent',
                    border: isActiveRoute('/Shipments') 
                      ? '1px solid rgba(102, 126, 234, 0.18)' 
                      : '1px solid transparent',
                    borderRadius: '8px',
                    margin: '2px 0',
                    padding: '9px 11px',
                    '&:hover': {
                      backgroundColor: isActiveRoute('/Shipments') 
                        ? 'rgba(102, 126, 234, 0.1)' 
                        : 'rgba(102, 126, 234, 0.05)',
                      border: isActiveRoute('/Shipments') 
                        ? '1px solid rgba(102, 126, 234, 0.22)' 
                        : '1px solid rgba(102, 126, 234, 0.12)',
                      transform: 'translateX(2px)',
                    }
                  }}
                >
                  <ListItemDecorator>
                    <Box sx={{ 
                      backgroundColor: isActiveRoute('/Shipments') 
                        ? 'rgba(102, 126, 234, 0.1)'
                        : 'rgba(243, 244, 246, 0.6)',
                      borderRadius: '8px', 
                      padding: 0.6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                    }}>
                      <LocalShippingIcon sx={{ 
                        fontSize: 16, 
                        color: isActiveRoute('/Shipments') ? '#667eea' : '#6b7280',
                        transition: 'all 0.2s ease',
                      }} />
                    </Box>
                  </ListItemDecorator>
                  <Typography sx={{ 
                    fontSize: '14px', 
                    color: isActiveRoute('/Shipments') ? '#667eea' : '#6b7280',
                    fontWeight: isActiveRoute('/Shipments') ? '600' : '500',
                    letterSpacing: '0.05px',
                  }}>
                    {t('nav.listShipments')}
                  </Typography>
                </ListItemButton>
                </PermissionGate>
                <PermissionGate permissions={[PERMISSIONS.SHIPMENTS_CREATE]}>
                  <ListItemButton 
                    onClick={() => { setDrawerOpen(false); navigate("/shipment/AddShipment"); }}
                    sx={{
                      backgroundColor: isActiveRoute('/shipment/AddShipment') ? '#f3f4f6' : 'transparent',
                      border: isActiveRoute('/shipment/AddShipment') ? '1px solid #d1d5db' : '1px solid transparent',
                      borderRadius: '6px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: isActiveRoute('/shipment/AddShipment') ? '#f3f4f6' : '#f9fafb',
                        border: isActiveRoute('/shipment/AddShipment') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <Box sx={{ 
                        backgroundColor: isActiveRoute('/shipment/AddShipment') ? '#e5e7eb' : '#f3f4f6', 
                        borderRadius: '4px', 
                        padding: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <AddCircleIcon sx={{ fontSize: 14, color: isActiveRoute('/shipment/AddShipment') ? '#667eea' : '#6b7280' }} />
                      </Box>
                    </ListItemDecorator>
                    <Typography sx={{ 
                      fontSize: '13px', 
                      color: isActiveRoute('/shipment/AddShipment') ? '#111827' : '#6b7280',
                      fontWeight: isActiveRoute('/shipment/AddShipment') ? '500' : '400'
                    }}>
                      {t('nav.addShipment')}
                    </Typography>
                  </ListItemButton>
                </PermissionGate>
                <PermissionGate permissions={[PERMISSIONS.SHIPMENTS_UPLOAD_EXCEL]}>
                  <ListItemButton 
                    onClick={() => { setDrawerOpen(false); navigate("/shipment/import"); }}
                    sx={{
                      backgroundColor: isActiveRoute('/shipment/import') ? '#f3f4f6' : 'transparent',
                      border: isActiveRoute('/shipment/import') ? '1px solid #d1d5db' : '1px solid transparent',
                      borderRadius: '6px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: isActiveRoute('/shipment/import') ? '#f3f4f6' : '#f9fafb',
                        border: isActiveRoute('/shipment/import') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <Box sx={{ 
                        backgroundColor: isActiveRoute('/shipment/import') ? '#e5e7eb' : '#f3f4f6', 
                        borderRadius: '4px', 
                        padding: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <FileUploadIcon sx={{ fontSize: 14, color: isActiveRoute('/shipment/import') ? '#667eea' : '#6b7280' }} />
                      </Box>
                    </ListItemDecorator>
                    <Typography sx={{ 
                      fontSize: '13px', 
                      color: isActiveRoute('/shipment/import') ? '#111827' : '#6b7280',
                      fontWeight: isActiveRoute('/shipment/import') ? '500' : '400'
                    }}>
                      {t('nav.importShipments')}
                    </Typography>
                  </ListItemButton>
                </PermissionGate>
                {shipmentScanningNavSection.show && (
                  <NavSectionLabel
                    label={t('nav.sectionScanning')}
                    nested
                    separated={shipmentScanningNavSection.separated}
                  />
                )}
                <PermissionGate permission={PERMISSIONS.SHIPMENT_PIECES_SCAN_UPDATE}>
                <ListItemButton
                  onClick={() => { setDrawerOpen(false); navigate("/shipment/pieces/scan"); }}
                  sx={{
                    backgroundColor: isActiveRoute('/shipment/pieces/scan') ? '#f3f4f6' : 'transparent',
                    border: isActiveRoute('/shipment/pieces/scan') ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '6px',
                    margin: '2px 0',
                    '&:hover': {
                      backgroundColor: isActiveRoute('/shipment/pieces/scan') ? '#f3f4f6' : '#f9fafb',
                      border: isActiveRoute('/shipment/pieces/scan') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                    }
                  }}
                >
                  <ListItemDecorator>
                    <Box sx={{
                      backgroundColor: isActiveRoute('/shipment/pieces/scan') ? '#e5e7eb' : '#f3f4f6',
                      borderRadius: '4px',
                      padding: 0.4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <QrCodeScannerIcon sx={{ fontSize: 14, color: isActiveRoute('/shipment/pieces/scan') ? '#667eea' : '#6b7280' }} />
                    </Box>
                  </ListItemDecorator>
                  <Typography sx={{
                    fontSize: '13px',
                    color: isActiveRoute('/shipment/pieces/scan') ? '#111827' : '#6b7280',
                    fontWeight: isActiveRoute('/shipment/pieces/scan') ? '500' : '400'
                  }}>
                    {t('nav.scanPiece')}
                  </Typography>
                </ListItemButton>
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.SHIPMENTS_SCAN_UPDATE}>
                <ListItemButton 
                  onClick={() => { setDrawerOpen(false); navigate("/shipment/scan"); }}
                  sx={{
                    backgroundColor: isActiveRoute('/shipment/scan') ? '#f3f4f6' : 'transparent',
                    border: isActiveRoute('/shipment/scan') ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '6px',
                    margin: '2px 0',
                    '&:hover': {
                      backgroundColor: isActiveRoute('/shipment/scan') ? '#f3f4f6' : '#f9fafb',
                      border: isActiveRoute('/shipment/scan') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                    }
                  }}
                >
                  <ListItemDecorator>
                    <Box sx={{ 
                      backgroundColor: isActiveRoute('/shipment/scan') ? '#e5e7eb' : '#f3f4f6', 
                      borderRadius: '4px', 
                      padding: 0.4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <QrCodeScannerIcon sx={{ fontSize: 14, color: isActiveRoute('/shipment/scan') ? '#667eea' : '#6b7280' }} />
                    </Box>
                  </ListItemDecorator>
                  <Typography sx={{ 
                    fontSize: '13px', 
                    color: isActiveRoute('/shipment/scan') ? '#111827' : '#6b7280',
                    fontWeight: isActiveRoute('/shipment/scan') ? '500' : '400'
                  }}>
                    {t('nav.scanShipments')}
                  </Typography>
                </ListItemButton>
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.SCAN_RESOLVE}>
                <ListItemButton
                  onClick={() => { setDrawerOpen(false); navigate("/scan"); }}
                  sx={{
                    backgroundColor: isActiveRoute('/scan') ? '#f3f4f6' : 'transparent',
                    border: isActiveRoute('/scan') ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '6px',
                    margin: '2px 0',
                    '&:hover': {
                      backgroundColor: isActiveRoute('/scan') ? '#f3f4f6' : '#f9fafb',
                      border: isActiveRoute('/scan') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                    }
                  }}
                >
                  <ListItemDecorator>
                    <Box sx={{
                      backgroundColor: isActiveRoute('/scan') ? '#e5e7eb' : '#f3f4f6',
                      borderRadius: '4px',
                      padding: 0.4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <QrCodeScannerIcon sx={{ fontSize: 14, color: isActiveRoute('/scan') ? '#667eea' : '#6b7280' }} />
                    </Box>
                  </ListItemDecorator>
                  <Typography sx={{
                    fontSize: '13px',
                    color: isActiveRoute('/scan') ? '#111827' : '#6b7280',
                    fontWeight: isActiveRoute('/scan') ? '500' : '400'
                  }}>
                    {t('nav.universalScanner')}
                  </Typography>
                </ListItemButton>
                </PermissionGate>
                {shipmentTrackingNavSection.show && (
                  <NavSectionLabel
                    label={t('nav.sectionTracking')}
                    nested
                    separated={shipmentTrackingNavSection.separated}
                  />
                )}
                <PermissionGate permissions={[PERMISSIONS.SHIPMENT_PIECE_TRACKING_EVENTS_LIST]}>
                  <ListItemButton
                    onClick={() => { setDrawerOpen(false); navigate("/shipment/piece-tracking-events"); }}
                    sx={{
                      backgroundColor: isActiveRoute('/shipment/piece-tracking-events') ? '#f3f4f6' : 'transparent',
                      border: isActiveRoute('/shipment/piece-tracking-events') ? '1px solid #d1d5db' : '1px solid transparent',
                      borderRadius: '6px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: isActiveRoute('/shipment/piece-tracking-events') ? '#f3f4f6' : '#f9fafb',
                        border: isActiveRoute('/shipment/piece-tracking-events') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <Box sx={{
                        backgroundColor: isActiveRoute('/shipment/piece-tracking-events') ? '#e5e7eb' : '#f3f4f6',
                        borderRadius: '4px',
                        padding: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <TimelineIcon sx={{ fontSize: 14, color: isActiveRoute('/shipment/piece-tracking-events') ? '#667eea' : '#6b7280' }} />
                      </Box>
                    </ListItemDecorator>
                    <Typography sx={{
                      fontSize: '13px',
                      color: isActiveRoute('/shipment/piece-tracking-events') ? '#111827' : '#6b7280',
                      fontWeight: isActiveRoute('/shipment/piece-tracking-events') ? '500' : '400'
                    }}>
                      {t('settingsPages.rolesPermsEditor.pieceTrackingEvents')}
                    </Typography>
                  </ListItemButton>
                </PermissionGate>
                <PermissionGate permissions={[PERMISSIONS.TRACKING_EVENTS_LIST]}>
                  <ListItemButton 
                    onClick={() => { setDrawerOpen(false); navigate("/shipment/tracking-events"); }}
                    sx={{
                      backgroundColor: isActiveRoute('/shipment/tracking-events') ? '#f3f4f6' : 'transparent',
                      border: isActiveRoute('/shipment/tracking-events') ? '1px solid #d1d5db' : '1px solid transparent',
                      borderRadius: '6px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: isActiveRoute('/shipment/tracking-events') ? '#f3f4f6' : '#f9fafb',
                        border: isActiveRoute('/shipment/tracking-events') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <Box sx={{ 
                        backgroundColor: isActiveRoute('/shipment/tracking-events') ? '#e5e7eb' : '#f3f4f6', 
                        borderRadius: '4px', 
                        padding: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <TimelineIcon sx={{ fontSize: 14, color: isActiveRoute('/shipment/tracking-events') ? '#667eea' : '#6b7280' }} />
                      </Box>
                    </ListItemDecorator>
                    <Typography sx={{ 
                      fontSize: '13px', 
                      color: isActiveRoute('/shipment/tracking-events') ? '#111827' : '#6b7280',
                      fontWeight: isActiveRoute('/shipment/tracking-events') ? '500' : '400'
                    }}>
                      {t('nav.shipmentTrackingEvent')}
                    </Typography>
                  </ListItemButton>
                </PermissionGate>
                <PermissionGate permissions={[PERMISSIONS.SHIPMENT_UNIT_TRACKING_EVENTS_LIST]}>
                  <ListItemButton 
                    onClick={() => { setDrawerOpen(false); navigate("/shipment/unit-tracking-events"); }}
                    sx={{
                      backgroundColor: isActiveRoute('/shipment/unit-tracking-events') ? '#f3f4f6' : 'transparent',
                      border: isActiveRoute('/shipment/unit-tracking-events') ? '1px solid #d1d5db' : '1px solid transparent',
                      borderRadius: '6px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: isActiveRoute('/shipment/unit-tracking-events') ? '#f3f4f6' : '#f9fafb',
                        border: isActiveRoute('/shipment/unit-tracking-events') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <Box sx={{ 
                        backgroundColor: isActiveRoute('/shipment/unit-tracking-events') ? '#e5e7eb' : '#f3f4f6', 
                        borderRadius: '4px', 
                        padding: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <TimelineIcon sx={{ fontSize: 14, color: isActiveRoute('/shipment/unit-tracking-events') ? '#667eea' : '#6b7280' }} />
                      </Box>
                    </ListItemDecorator>
                    <Typography sx={{ 
                      fontSize: '13px', 
                      color: isActiveRoute('/shipment/unit-tracking-events') ? '#111827' : '#6b7280',
                      fontWeight: isActiveRoute('/shipment/unit-tracking-events') ? '500' : '400'
                    }}>
                      {t('nav.unitTrackingEvents')}
                    </Typography>
                  </ListItemButton>
                </PermissionGate>
                {shipmentInventoryNavSection.show && (
                  <NavSectionLabel
                    label={t('nav.sectionUnitsAndPieces')}
                    nested
                    separated={shipmentInventoryNavSection.separated}
                  />
                )}
                <PermissionGate permissions={[PERMISSIONS.SHIPMENT_PIECES_LIST]}>
                  <ListItemButton
                    onClick={() => { setDrawerOpen(false); navigate("/shipment/pieces"); }}
                    sx={{
                      backgroundColor: isActiveRoute('/shipment/pieces') ? '#f3f4f6' : 'transparent',
                      border: isActiveRoute('/shipment/pieces') ? '1px solid #d1d5db' : '1px solid transparent',
                      borderRadius: '6px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: isActiveRoute('/shipment/pieces') ? '#f3f4f6' : '#f9fafb',
                        border: isActiveRoute('/shipment/pieces') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <Box sx={{
                        backgroundColor: isActiveRoute('/shipment/pieces') ? '#e5e7eb' : '#f3f4f6',
                        borderRadius: '4px',
                        padding: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <InventoryIcon sx={{ fontSize: 14, color: isActiveRoute('/shipment/pieces') ? '#667eea' : '#6b7280' }} />
                      </Box>
                    </ListItemDecorator>
                    <Typography sx={{
                      fontSize: '13px',
                      color: isActiveRoute('/shipment/pieces') ? '#111827' : '#6b7280',
                      fontWeight: isActiveRoute('/shipment/pieces') ? '500' : '400'
                    }}>
                      {t('nav.shipmentPieces')}
                    </Typography>
                  </ListItemButton>
                </PermissionGate>
                <PermissionGate permissions={[PERMISSIONS.RECEIVER_DETAILS_LIST]}>
                  <ListItemButton 
                    onClick={() => { setDrawerOpen(false); navigate("/shipment/receiver-details"); }}
                    sx={{
                      backgroundColor: isActiveRoute('/shipment/receiver-details') ? '#f3f4f6' : 'transparent',
                      border: isActiveRoute('/shipment/receiver-details') ? '1px solid #d1d5db' : '1px solid transparent',
                      borderRadius: '6px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: isActiveRoute('/shipment/receiver-details') ? '#f3f4f6' : '#f9fafb',
                        border: isActiveRoute('/shipment/receiver-details') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <Box sx={{ 
                        backgroundColor: isActiveRoute('/shipment/receiver-details') ? '#e5e7eb' : '#f3f4f6', 
                        borderRadius: '4px', 
                        padding: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <PersonIcon sx={{ fontSize: 14, color: isActiveRoute('/shipment/receiver-details') ? '#667eea' : '#6b7280' }} />
                      </Box>
                    </ListItemDecorator>
                    <Typography sx={{ 
                      fontSize: '13px', 
                      color: isActiveRoute('/shipment/receiver-details') ? '#111827' : '#6b7280',
                      fontWeight: isActiveRoute('/shipment/receiver-details') ? '500' : '400'
                    }}>
                      {t('nav.receiverDetails')}
                    </Typography>
                  </ListItemButton>
                </PermissionGate>
                <PermissionGate permissions={[PERMISSIONS.SHIPMENT_UNITS_LIST]}>
                  <ListItemButton 
                    onClick={() => { setDrawerOpen(false); navigate("/shipment/units"); }}
                    sx={{
                      backgroundColor: isActiveRoute('/shipment/units') ? '#f3f4f6' : 'transparent',
                      border: isActiveRoute('/shipment/units') ? '1px solid #d1d5db' : '1px solid transparent',
                      borderRadius: '6px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: isActiveRoute('/shipment/units') ? '#f3f4f6' : '#f9fafb',
                        border: isActiveRoute('/shipment/units') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <Box sx={{ 
                        backgroundColor: isActiveRoute('/shipment/units') ? '#e5e7eb' : '#f3f4f6', 
                        borderRadius: '4px', 
                        padding: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <InventoryIcon sx={{ fontSize: 14, color: isActiveRoute('/shipment/units') ? '#667eea' : '#6b7280' }} />
                      </Box>
                    </ListItemDecorator>
                    <Typography sx={{ 
                      fontSize: '13px', 
                      color: isActiveRoute('/shipment/units') ? '#111827' : '#6b7280',
                      fontWeight: isActiveRoute('/shipment/units') ? '500' : '400'
                    }}>
                      {t('nav.shipmentUnits')}
                    </Typography>
                  </ListItemButton>
                </PermissionGate>
              </List>
            </Collapse>
            </Box>
          </PermissionGate>

          {financeNavSection.show && (
            <NavSectionLabel
              label={t('nav.sectionFinance')}
              separated={financeNavSection.separated}
            />
          )}
          {/* Pricing plans — parent row; Pricing rules sits directly under it */}
          <PermissionGate permissions={[PERMISSIONS.PRICING_PLANS_LIST]}>
            <ListItemButton 
              onClick={() => { setDrawerOpen(false); navigate("/PricingPlans"); }}
              sx={{
                backgroundColor: isActiveRoute('/PricingPlans') ? '#f3f4f6' : 'transparent',
                border: isActiveRoute('/PricingPlans') ? '1px solid #d1d5db' : '1px solid transparent',
                '&:hover': {
                  backgroundColor: isActiveRoute('/PricingPlans') ? '#f3f4f6' : '#f9fafb',
                  border: isActiveRoute('/PricingPlans') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                }
              }}
            >
              <ListItemDecorator>
                <Box sx={{ 
                  backgroundColor: isActiveRoute('/PricingPlans') ? '#e5e7eb' : '#f3f4f6', 
                  borderRadius: '6px', 
                  padding: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AttachMoneyIcon sx={{ fontSize: 18, color: isActiveRoute('/PricingPlans') ? '#667eea' : '#6b7280' }} />
                </Box>
              </ListItemDecorator>
              <Typography sx={{ 
                fontWeight: '500', 
                color: isActiveRoute('/PricingPlans') ? '#111827' : '#374151' 
              }}>
                {t('nav.pricingPlans')}
              </Typography>
            </ListItemButton>
          </PermissionGate>

          {/* Pricing rules — /PricingRules, listed under Pricing plans */}
          <PermissionGate permissions={[PERMISSIONS.PRICING_RULES_LIST]}>
            <ListItemButton
              onClick={() => { setDrawerOpen(false); navigate('/PricingRules'); }}
              sx={{
                backgroundColor: isActiveRoute('/PricingRules') ? '#f3f4f6' : 'transparent',
                border: isActiveRoute('/PricingRules') ? '1px solid #d1d5db' : '1px solid transparent',
                '&:hover': {
                  backgroundColor: isActiveRoute('/PricingRules') ? '#f3f4f6' : '#f9fafb',
                  border: isActiveRoute('/PricingRules') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                }
              }}
            >
              <ListItemDecorator>
                <Box sx={{
                  backgroundColor: isActiveRoute('/PricingRules') ? '#e5e7eb' : '#f3f4f6',
                  borderRadius: '6px',
                  padding: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <RuleIcon sx={{ fontSize: 18, color: isActiveRoute('/PricingRules') ? '#667eea' : '#6b7280' }} />
                </Box>
              </ListItemDecorator>
              <Typography sx={{
                fontWeight: '500',
                color: isActiveRoute('/PricingRules') ? '#111827' : '#374151'
              }}>
                {t('nav.pricingRules')}
              </Typography>
            </ListItemButton>
          </PermissionGate>

          {/* Invoices */}
          <PermissionGate permissions={[PERMISSIONS.INVOICES_LIST]}>
            <ListItemButton 
              onClick={() => { setDrawerOpen(false); navigate("/Invoices"); }}
              sx={{
                backgroundColor: isActiveRoute('/Invoices') ? '#f3f4f6' : 'transparent',
                border: isActiveRoute('/Invoices') ? '1px solid #d1d5db' : '1px solid transparent',
                '&:hover': {
                  backgroundColor: isActiveRoute('/Invoices') ? '#f3f4f6' : '#f9fafb',
                  border: isActiveRoute('/Invoices') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                }
              }}
            >
              <ListItemDecorator>
                <Box sx={{ 
                  backgroundColor: isActiveRoute('/Invoices') ? '#e5e7eb' : '#f3f4f6', 
                  borderRadius: '6px', 
                  padding: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ReceiptIcon sx={{ fontSize: 18, color: isActiveRoute('/Invoices') ? '#667eea' : '#6b7280' }} />
                </Box>
              </ListItemDecorator>
              <Typography sx={{ 
                fontWeight: '500', 
                color: isActiveRoute('/Invoices') ? '#111827' : '#374151' 
              }}>
                {t('nav.invoices')}
              </Typography>
            </ListItemButton>
          </PermissionGate>

          {/* Payments */}
          <PermissionGate permissions={[PERMISSIONS.PAYMENTS_LIST]}>
            <ListItemButton 
              onClick={() => { setDrawerOpen(false); navigate("/payments"); }}
              sx={{
                backgroundColor: isActiveRoute('/payments') ? '#f3f4f6' : 'transparent',
                border: isActiveRoute('/payments') ? '1px solid #d1d5db' : '1px solid transparent',
                '&:hover': {
                  backgroundColor: isActiveRoute('/payments') ? '#f3f4f6' : '#f9fafb',
                  border: isActiveRoute('/payments') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                }
              }}
            >
              <ListItemDecorator>
                <Box sx={{ 
                  backgroundColor: isActiveRoute('/payments') ? '#e5e7eb' : '#f3f4f6', 
                  borderRadius: '6px', 
                  padding: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <PaymentIcon sx={{ fontSize: 18, color: isActiveRoute('/payments') ? '#667eea' : '#6b7280' }} />
                </Box>
              </ListItemDecorator>
              <Typography sx={{ 
                fontWeight: '500', 
                color: isActiveRoute('/payments') ? '#111827' : '#374151' 
              }}>
                {t('nav.payments')}
              </Typography>
            </ListItemButton>
          </PermissionGate>

          {customersNavSection.show && (
            <NavSectionLabel
              label={t('nav.sectionCustomers')}
              separated={customersNavSection.separated}
            />
          )}
          {/* Customers — list + optional registration approval queue */}
          <PermissionGate permissions={CUSTOMERS_NAV_PERMISSIONS} requireAll={false}>
            {showCustomersAccordion ? (
              <Box ref={customersSectionRef}>
                <ListItemButton
                  onClick={() => setCustomersOpen(!customersOpen)}
                  sx={{
                    backgroundColor: isCustomersNavSectionActive ? 'rgba(102, 126, 234, 0.08)' : 'transparent',
                    border: isCustomersNavSectionActive
                      ? '1px solid rgba(102, 126, 234, 0.2)'
                      : '1px solid transparent',
                    position: 'relative',
                    '&::before': isCustomersNavSectionActive
                      ? {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '3px',
                          height: '50%',
                          backgroundColor: '#667eea',
                          borderRadius: '0 3px 3px 0',
                        }
                      : {},
                    '&:hover': {
                      backgroundColor: isCustomersNavSectionActive
                        ? 'rgba(102, 126, 234, 0.1)'
                        : 'rgba(102, 126, 234, 0.06)',
                      border: isCustomersNavSectionActive
                        ? '1px solid rgba(102, 126, 234, 0.25)'
                        : '1px solid rgba(102, 126, 234, 0.15)',
                    },
                  }}
                >
                  <ListItemDecorator>
                    <Box
                      sx={{
                        backgroundColor: isCustomersNavSectionActive
                          ? 'rgba(102, 126, 234, 0.12)'
                          : 'rgba(243, 244, 246, 0.8)',
                        borderRadius: '10px',
                        padding: 0.9,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <GroupIcon
                        sx={{
                          fontSize: 20,
                          color: isCustomersNavSectionActive ? '#667eea' : '#6b7280',
                          transition: 'all 0.2s ease',
                        }}
                      />
                    </Box>
                  </ListItemDecorator>
                  <Typography
                    sx={{
                      fontWeight: isCustomersNavSectionActive ? '600' : '500',
                      fontSize: '15px',
                      color: isCustomersNavSectionActive ? '#667eea' : '#374151',
                      letterSpacing: '0.1px',
                      flexGrow: 1,
                    }}
                  >
                    {t('nav.customers')}
                  </Typography>
                  {customersOpen ? (
                    <ExpandLessIcon sx={{ color: '#667eea', fontSize: 20, transition: 'transform 0.2s ease' }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: '#6b7280', fontSize: 20, transition: 'transform 0.2s ease' }} />
                  )}
                </ListItemButton>
                <Collapse in={customersOpen} timeout="auto" unmountOnExit>
                  <List sx={{ pl: 3, padding: '4px 8px', mt: 0.5 }}>
                    <PermissionGate permissions={[PERMISSIONS.CUSTOMERS_LIST]}>
                      <ListItemButton
                        onClick={() => {
                          setDrawerOpen(false);
                          navigate('/Customers');
                        }}
                        sx={{
                          backgroundColor: isActiveRoute('/Customers')
                            ? 'rgba(102, 126, 234, 0.08)'
                            : 'transparent',
                          border: isActiveRoute('/Customers')
                            ? '1px solid rgba(102, 126, 234, 0.18)'
                            : '1px solid transparent',
                          borderRadius: '8px',
                          margin: '2px 0',
                          padding: '9px 11px',
                          '&:hover': {
                            backgroundColor: isActiveRoute('/Customers')
                              ? 'rgba(102, 126, 234, 0.1)'
                              : 'rgba(102, 126, 234, 0.05)',
                            border: isActiveRoute('/Customers')
                              ? '1px solid rgba(102, 126, 234, 0.22)'
                              : '1px solid rgba(102, 126, 234, 0.12)',
                            transform: 'translateX(2px)',
                          },
                        }}
                      >
                        <ListItemDecorator>
                          <Box
                            sx={{
                              backgroundColor: isActiveRoute('/Customers')
                                ? 'rgba(102, 126, 234, 0.1)'
                                : 'rgba(243, 244, 246, 0.6)',
                              borderRadius: '8px',
                              padding: 0.6,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <GroupIcon
                              sx={{
                                fontSize: 16,
                                color: isActiveRoute('/Customers') ? '#667eea' : '#6b7280',
                                transition: 'all 0.2s ease',
                              }}
                            />
                          </Box>
                        </ListItemDecorator>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            color: isActiveRoute('/Customers') ? '#667eea' : '#6b7280',
                            fontWeight: isActiveRoute('/Customers') ? '600' : '500',
                            letterSpacing: '0.05px',
                          }}
                        >
                          {t('nav.listCustomers')}
                        </Typography>
                      </ListItemButton>
                    </PermissionGate>
                    <PermissionGate permissions={[PERMISSIONS.CUSTOMERS_APPROVE]}>
                      <ListItemButton
                        onClick={() => {
                          setDrawerOpen(false);
                          navigate('/customers/pending-registrations');
                        }}
                        sx={{
                          backgroundColor: isActiveRoute('/customers/pending-registrations')
                            ? 'rgba(102, 126, 234, 0.08)'
                            : 'transparent',
                          border: isActiveRoute('/customers/pending-registrations')
                            ? '1px solid rgba(102, 126, 234, 0.18)'
                            : '1px solid transparent',
                          borderRadius: '8px',
                          margin: '2px 0',
                          padding: '9px 11px',
                          '&:hover': {
                            backgroundColor: isActiveRoute('/customers/pending-registrations')
                              ? 'rgba(102, 126, 234, 0.1)'
                              : 'rgba(102, 126, 234, 0.05)',
                            border: isActiveRoute('/customers/pending-registrations')
                              ? '1px solid rgba(102, 126, 234, 0.22)'
                              : '1px solid rgba(102, 126, 234, 0.12)',
                            transform: 'translateX(2px)',
                          },
                        }}
                      >
                        <ListItemDecorator>
                          <Box
                            sx={{
                              backgroundColor: isActiveRoute('/customers/pending-registrations')
                                ? 'rgba(102, 126, 234, 0.1)'
                                : 'rgba(243, 244, 246, 0.6)',
                              borderRadius: '8px',
                              padding: 0.6,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <HowToRegIcon
                              sx={{
                                fontSize: 16,
                                color: isActiveRoute('/customers/pending-registrations') ? '#667eea' : '#6b7280',
                                transition: 'all 0.2s ease',
                              }}
                            />
                          </Box>
                        </ListItemDecorator>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            color: isActiveRoute('/customers/pending-registrations') ? '#667eea' : '#6b7280',
                            fontWeight: isActiveRoute('/customers/pending-registrations') ? '600' : '500',
                            letterSpacing: '0.05px',
                          }}
                        >
                          {t('nav.customerRegistrationApprovals')}
                        </Typography>
                      </ListItemButton>
                    </PermissionGate>
                  </List>
                </Collapse>
              </Box>
            ) : customerListAllowed ? (
              <ListItemButton
                onClick={() => {
                  setDrawerOpen(false);
                  navigate('/Customers');
                }}
                sx={{
                  backgroundColor: isActiveRoute('/Customers') ? '#f3f4f6' : 'transparent',
                  border: isActiveRoute('/Customers') ? '1px solid #d1d5db' : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: isActiveRoute('/Customers') ? '#f3f4f6' : '#f9fafb',
                    border: isActiveRoute('/Customers') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                  },
                }}
              >
                <ListItemDecorator>
                  <Box
                    sx={{
                      backgroundColor: isActiveRoute('/Customers') ? '#e5e7eb' : '#f3f4f6',
                      borderRadius: '6px',
                      padding: 0.8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <GroupIcon sx={{ fontSize: 18, color: isActiveRoute('/Customers') ? '#667eea' : '#6b7280' }} />
                  </Box>
                </ListItemDecorator>
                <Typography
                  sx={{
                    fontWeight: '500',
                    color: isActiveRoute('/Customers') ? '#111827' : '#374151',
                  }}
                >
                  {t('nav.customers')}
                </Typography>
              </ListItemButton>
            ) : (
              <ListItemButton
                onClick={() => {
                  setDrawerOpen(false);
                  navigate('/customers/pending-registrations');
                }}
                sx={{
                  backgroundColor: isActiveRoute('/customers/pending-registrations') ? '#f3f4f6' : 'transparent',
                  border: isActiveRoute('/customers/pending-registrations') ? '1px solid #d1d5db' : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: isActiveRoute('/customers/pending-registrations') ? '#f3f4f6' : '#f9fafb',
                    border: isActiveRoute('/customers/pending-registrations') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                  },
                }}
              >
                <ListItemDecorator>
                  <Box
                    sx={{
                      backgroundColor: isActiveRoute('/customers/pending-registrations') ? '#e5e7eb' : '#f3f4f6',
                      borderRadius: '6px',
                      padding: 0.8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <HowToRegIcon
                      sx={{
                        fontSize: 18,
                        color: isActiveRoute('/customers/pending-registrations') ? '#667eea' : '#6b7280',
                      }}
                    />
                  </Box>
                </ListItemDecorator>
                <Typography
                  sx={{
                    fontWeight: '500',
                    color: isActiveRoute('/customers/pending-registrations') ? '#111827' : '#374151',
                  }}
                >
                  {t('nav.customerRegistrationApprovals')}
                </Typography>
              </ListItemButton>
            )}
          </PermissionGate>

          {settingsNavSection.show && (
            <NavSectionLabel
              label={t('nav.sectionSettings')}
              separated={settingsNavSection.separated}
            />
          )}
          {/* Settings */}
          <PermissionGate permissions={SETTINGS_NAV_PERMISSIONS}>
            <Box ref={settingsSectionRef}>
            <ListItemButton 
              onClick={() => setSettingsOpen(!settingsOpen)}
              sx={{
                backgroundColor: isActiveRoute('/settings') ? '#f3f4f6' : 'transparent',
                border: isActiveRoute('/settings') ? '1px solid #d1d5db' : '1px solid transparent',
                '&:hover': {
                  backgroundColor: isActiveRoute('/settings') ? '#f3f4f6' : '#f9fafb',
                  border: isActiveRoute('/settings') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                }
              }}
            >
              <ListItemDecorator>
                <Box sx={{ 
                  backgroundColor: isActiveRoute('/settings') ? '#e5e7eb' : '#f3f4f6', 
                  borderRadius: '6px', 
                  padding: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <SettingsIcon sx={{ fontSize: 18, color: isActiveRoute('/settings') ? '#667eea' : '#6b7280' }} />
                </Box>
              </ListItemDecorator>
              <Typography sx={{ 
                fontWeight: '500', 
                color: isActiveRoute('/settings') ? '#111827' : '#374151', 
                flexGrow: 1 
              }}>
                {t('nav.settings')}
              </Typography>
              {settingsOpen ? <ExpandLessIcon sx={{ color: '#6b7280' }} /> : <ExpandMoreIcon sx={{ color: '#6b7280' }} />}
            </ListItemButton>
            <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
              <List sx={{ pl: 2, padding: '0 8px' }}>
                {settingsSiteNavSection.show && (
                  <NavSectionLabel
                    label={t('nav.sectionSiteConfig')}
                    nested
                    separated={settingsSiteNavSection.separated}
                  />
                )}
                <PermissionGate permissions={[PERMISSIONS.SETTINGS_LIST]}>
                <ListItemButton 
                  onClick={() => { setDrawerOpen(false); navigate("/settings/general"); }}
                  sx={{
                    backgroundColor: isActiveRoute('/settings/general') ? '#f3f4f6' : 'transparent',
                    border: isActiveRoute('/settings/general') ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '6px',
                    margin: '2px 0',
                    '&:hover': {
                      backgroundColor: isActiveRoute('/settings/general') ? '#f3f4f6' : '#f9fafb',
                      border: isActiveRoute('/settings/general') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                    }
                  }}
                >
                  <ListItemDecorator>
                    <Box sx={{ 
                      backgroundColor: isActiveRoute('/settings/general') ? '#e5e7eb' : '#f3f4f6', 
                      borderRadius: '4px', 
                      padding: 0.4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <SettingsIcon sx={{ fontSize: 14, color: isActiveRoute('/settings/general') ? '#667eea' : '#6b7280' }} />
                    </Box>
                  </ListItemDecorator>
                  <Typography sx={{ 
                    fontSize: '13px', 
                    color: isActiveRoute('/settings/general') ? '#111827' : '#6b7280',
                    fontWeight: isActiveRoute('/settings/general') ? '500' : '400'
                  }}>
                    {t('nav.generalSettings')}
                  </Typography>
                </ListItemButton>
                <ListItemButton 
                  onClick={() => { setDrawerOpen(false); navigate("/settings/payment"); }}
                  sx={{
                    backgroundColor: isActiveRoute('/settings/payment') ? '#f3f4f6' : 'transparent',
                    border: isActiveRoute('/settings/payment') ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '6px',
                    margin: '2px 0',
                    '&:hover': {
                      backgroundColor: isActiveRoute('/settings/payment') ? '#f3f4f6' : '#f9fafb',
                      border: isActiveRoute('/settings/payment') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                    }
                  }}
                >
                  <ListItemDecorator>
                    <Box sx={{ 
                      backgroundColor: isActiveRoute('/settings/payment') ? '#e5e7eb' : '#f3f4f6', 
                      borderRadius: '4px', 
                      padding: 0.4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <PaymentIcon sx={{ fontSize: 14, color: isActiveRoute('/settings/payment') ? '#667eea' : '#6b7280' }} />
                    </Box>
                  </ListItemDecorator>
                  <Typography sx={{ 
                    fontSize: '13px', 
                    color: isActiveRoute('/settings/payment') ? '#111827' : '#6b7280',
                    fontWeight: isActiveRoute('/settings/payment') ? '500' : '400'
                  }}>
                    {t('nav.paymentSettings')}
                  </Typography>
                </ListItemButton>
                <ListItemButton
                  onClick={() => { setDrawerOpen(false); navigate("/settings/shipments-config"); }}
                  sx={{
                    backgroundColor: isActiveRoute('/settings/shipments-config') ? '#f3f4f6' : 'transparent',
                    border: isActiveRoute('/settings/shipments-config') ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '6px',
                    margin: '2px 0',
                    '&:hover': {
                      backgroundColor: isActiveRoute('/settings/shipments-config') ? '#f3f4f6' : '#f9fafb',
                      border: isActiveRoute('/settings/shipments-config') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                    }
                  }}
                >
                  <ListItemDecorator>
                    <Box sx={{
                      backgroundColor: isActiveRoute('/settings/shipments-config') ? '#e5e7eb' : '#f3f4f6',
                      borderRadius: '4px',
                      padding: 0.4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <InventoryIcon sx={{ fontSize: 14, color: isActiveRoute('/settings/shipments-config') ? '#667eea' : '#6b7280' }} />
                    </Box>
                  </ListItemDecorator>
                  <Typography sx={{
                    fontSize: '13px',
                    color: isActiveRoute('/settings/shipments-config') ? '#111827' : '#6b7280',
                    fontWeight: isActiveRoute('/settings/shipments-config') ? '500' : '400'
                  }}>
                    {t('nav.shipmentsConfig')}
                  </Typography>
                </ListItemButton>
                </PermissionGate>
                {settingsIntegrationsNavSection.show && (
                  <NavSectionLabel
                    label={t('nav.sectionIntegrations')}
                    nested
                    separated={settingsIntegrationsNavSection.separated}
                  />
                )}
                <PermissionGate permissions={[PERMISSIONS.SETTINGS_LIST]}>
                <ListItemButton 
                  onClick={() => { setDrawerOpen(false); navigate("/settings/twilio"); }}
                  sx={{
                    backgroundColor: isActiveRoute('/settings/twilio') ? '#f3f4f6' : 'transparent',
                    border: isActiveRoute('/settings/twilio') ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '6px',
                    margin: '2px 0',
                    '&:hover': {
                      backgroundColor: isActiveRoute('/settings/twilio') ? '#f3f4f6' : '#f9fafb',
                      border: isActiveRoute('/settings/twilio') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                    }
                  }}
                >
                  <ListItemDecorator>
                    <Box sx={{ 
                      backgroundColor: isActiveRoute('/settings/twilio') ? '#e5e7eb' : '#f3f4f6', 
                      borderRadius: '4px', 
                      padding: 0.4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <PhoneIcon2 sx={{ fontSize: 14, color: isActiveRoute('/settings/twilio') ? '#667eea' : '#6b7280' }} />
                    </Box>
                  </ListItemDecorator>
                  <Typography sx={{ 
                    fontSize: '13px', 
                    color: isActiveRoute('/settings/twilio') ? '#111827' : '#6b7280',
                    fontWeight: isActiveRoute('/settings/twilio') ? '500' : '400'
                  }}>
                    {t('nav.twilioSettings')}
                  </Typography>
                </ListItemButton>
                <ListItemButton 
                  onClick={() => { setDrawerOpen(false); navigate("/settings/whatsapp-messages"); }}
                  sx={{
                    backgroundColor: isActiveRoute('/settings/whatsapp-messages') ? '#f3f4f6' : 'transparent',
                    border: isActiveRoute('/settings/whatsapp-messages') ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '6px',
                    margin: '2px 0',
                    '&:hover': {
                      backgroundColor: isActiveRoute('/settings/whatsapp-messages') ? '#f3f4f6' : '#f9fafb',
                      border: isActiveRoute('/settings/whatsapp-messages') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                    }
                  }}
                >
                  <ListItemDecorator>
                    <Box sx={{ 
                      backgroundColor: isActiveRoute('/settings/whatsapp-messages') ? '#e5e7eb' : '#f3f4f6', 
                      borderRadius: '4px', 
                      padding: 0.4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <WhatsAppIcon sx={{ fontSize: 14, color: isActiveRoute('/settings/whatsapp-messages') ? '#667eea' : '#6b7280' }} />
                    </Box>
                  </ListItemDecorator>
                  <Typography sx={{ 
                    fontSize: '13px', 
                    color: isActiveRoute('/settings/whatsapp-messages') ? '#111827' : '#6b7280',
                    fontWeight: isActiveRoute('/settings/whatsapp-messages') ? '500' : '400'
                  }}>
                    {t('nav.whatsappMessages')}
                  </Typography>
                </ListItemButton>
                <ListItemButton 
                  onClick={() => { setDrawerOpen(false); navigate("/settings/whatsapp-chatbot"); }}
                  sx={{
                    backgroundColor: isActiveRoute('/settings/whatsapp-chatbot') ? '#f3f4f6' : 'transparent',
                    border: isActiveRoute('/settings/whatsapp-chatbot') ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '6px',
                    margin: '2px 0',
                    '&:hover': {
                      backgroundColor: isActiveRoute('/settings/whatsapp-chatbot') ? '#f3f4f6' : '#f9fafb',
                      border: isActiveRoute('/settings/whatsapp-chatbot') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                    }
                  }}
                >
                  <ListItemDecorator>
                    <Box sx={{ 
                      backgroundColor: isActiveRoute('/settings/whatsapp-chatbot') ? '#e5e7eb' : '#f3f4f6', 
                      borderRadius: '4px', 
                      padding: 0.4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <SmartToyIcon sx={{ fontSize: 14, color: isActiveRoute('/settings/whatsapp-chatbot') ? '#667eea' : '#6b7280' }} />
                    </Box>
                  </ListItemDecorator>
                  <Typography sx={{ 
                    fontSize: '13px', 
                    color: isActiveRoute('/settings/whatsapp-chatbot') ? '#111827' : '#6b7280',
                    fontWeight: isActiveRoute('/settings/whatsapp-chatbot') ? '500' : '400'
                  }}>
                    {t('nav.whatsappChatBot')}
                  </Typography>
                </ListItemButton>
                </PermissionGate>
                {settingsAccessNavSection.show && (
                  <NavSectionLabel
                    label={t('nav.sectionAccessControl')}
                    nested
                    separated={settingsAccessNavSection.separated}
                  />
                )}
                <PermissionGate permission={PERMISSIONS.PERMISSIONS_LIST}>
                  <ListItemButton 
                    onClick={() => { setDrawerOpen(false); navigate("/settings/permissions"); }}
                    sx={{
                      backgroundColor: isActiveRoute('/settings/permissions') ? '#f3f4f6' : 'transparent',
                      border: isActiveRoute('/settings/permissions') ? '1px solid #d1d5db' : '1px solid transparent',
                      borderRadius: '6px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: isActiveRoute('/settings/permissions') ? '#f3f4f6' : '#f9fafb',
                        border: isActiveRoute('/settings/permissions') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <Box sx={{ 
                        backgroundColor: isActiveRoute('/settings/permissions') ? '#e5e7eb' : '#f3f4f6', 
                        borderRadius: '4px', 
                        padding: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <SecurityIcon sx={{ fontSize: 14, color: isActiveRoute('/settings/permissions') ? '#667eea' : '#6b7280' }} />
                      </Box>
                    </ListItemDecorator>
                    <Typography sx={{ 
                      fontSize: '13px', 
                      color: isActiveRoute('/settings/permissions') ? '#111827' : '#6b7280',
                      fontWeight: isActiveRoute('/settings/permissions') ? '500' : '400'
                    }}>
                      {t('nav.permissions')}
                    </Typography>
                  </ListItemButton>
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.ROLES_LIST}>
                  <ListItemButton 
                    onClick={() => { setDrawerOpen(false); navigate("/settings/roles"); }}
                    sx={{
                      backgroundColor: isActiveRoute('/settings/roles') ? '#f3f4f6' : 'transparent',
                      border: isActiveRoute('/settings/roles') ? '1px solid #d1d5db' : '1px solid transparent',
                      borderRadius: '6px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: isActiveRoute('/settings/roles') ? '#f3f4f6' : '#f9fafb',
                        border: isActiveRoute('/settings/roles') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <Box sx={{ 
                        backgroundColor: isActiveRoute('/settings/roles') ? '#e5e7eb' : '#f3f4f6', 
                        borderRadius: '4px', 
                        padding: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <GroupIcon sx={{ fontSize: 14, color: isActiveRoute('/settings/roles') ? '#667eea' : '#6b7280' }} />
                      </Box>
                    </ListItemDecorator>
                    <Typography sx={{ 
                      fontSize: '13px', 
                      color: isActiveRoute('/settings/roles') ? '#111827' : '#6b7280',
                      fontWeight: isActiveRoute('/settings/roles') ? '500' : '400'
                    }}>
                      {t('nav.roles')}
                    </Typography>
                  </ListItemButton>
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.USERS_LIST}>
                  <ListItemButton 
                    onClick={() => { setDrawerOpen(false); navigate("/settings/users"); }}
                    sx={{
                      backgroundColor: isActiveRoute('/settings/users') ? '#f3f4f6' : 'transparent',
                      border: isActiveRoute('/settings/users') ? '1px solid #d1d5db' : '1px solid transparent',
                      borderRadius: '6px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: isActiveRoute('/settings/users') ? '#f3f4f6' : '#f9fafb',
                        border: isActiveRoute('/settings/users') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <Box sx={{ 
                        backgroundColor: isActiveRoute('/settings/users') ? '#e5e7eb' : '#f3f4f6', 
                        borderRadius: '4px', 
                        padding: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <PersonIcon sx={{ fontSize: 14, color: isActiveRoute('/settings/users') ? '#667eea' : '#6b7280' }} />
                      </Box>
                    </ListItemDecorator>
                    <Typography sx={{ 
                      fontSize: '13px', 
                      color: isActiveRoute('/settings/users') ? '#111827' : '#6b7280',
                      fontWeight: isActiveRoute('/settings/users') ? '500' : '400'
                    }}>
                      {t('nav.users')}
                    </Typography>
                  </ListItemButton>
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.USER_ROLES_LIST}>
                  <ListItemButton 
                    onClick={() => { setDrawerOpen(false); navigate("/settings/user-roles"); }}
                    sx={{
                      backgroundColor: isActiveRoute('/settings/user-roles') ? '#f3f4f6' : 'transparent',
                      border: isActiveRoute('/settings/user-roles') ? '1px solid #d1d5db' : '1px solid transparent',
                      borderRadius: '6px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: isActiveRoute('/settings/user-roles') ? '#f3f4f6' : '#f9fafb',
                        border: isActiveRoute('/settings/user-roles') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <Box sx={{ 
                        backgroundColor: isActiveRoute('/settings/user-roles') ? '#e5e7eb' : '#f3f4f6', 
                        borderRadius: '4px', 
                        padding: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <AssignmentIcon sx={{ fontSize: 14, color: isActiveRoute('/settings/user-roles') ? '#667eea' : '#6b7280' }} />
                      </Box>
                    </ListItemDecorator>
                    <Typography sx={{ 
                      fontSize: '13px', 
                      color: isActiveRoute('/settings/user-roles') ? '#111827' : '#6b7280',
                      fontWeight: isActiveRoute('/settings/user-roles') ? '500' : '400'
                    }}>
                      {t('nav.userRoleAssignments')}
                    </Typography>
                  </ListItemButton>
                </PermissionGate>

                {settingsMasterNavSection.show && (
                  <NavSectionLabel
                    label={t('nav.sectionMasterData')}
                    nested
                    separated={settingsMasterNavSection.separated}
                  />
                )}
                {/* Master — one list permission per route opens that page */}
                <PermissionGate permissions={MASTER_SETTINGS_OPEN_PAGE_PERMISSIONS}>
                  <Box ref={masterSectionRef}>
                  <ListItemButton 
                    onClick={() => setMasterOpen(!masterOpen)}
                    sx={{
                      backgroundColor: isMasterSettingsActive ? '#f3f4f6' : 'transparent',
                      border: isMasterSettingsActive ? '1px solid #d1d5db' : '1px solid transparent',
                      borderRadius: '6px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: isMasterSettingsActive ? '#f3f4f6' : '#f9fafb',
                        border: isMasterSettingsActive ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <Box sx={{ 
                        backgroundColor: isMasterSettingsActive ? '#e5e7eb' : '#f3f4f6', 
                        borderRadius: '4px', 
                        padding: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <CategoryIcon sx={{ fontSize: 14, color: isMasterSettingsActive ? '#667eea' : '#6b7280' }} />
                      </Box>
                    </ListItemDecorator>
                    <Typography sx={{ 
                      fontSize: '13px', 
                      color: isMasterSettingsActive ? '#111827' : '#6b7280',
                      fontWeight: isMasterSettingsActive ? '500' : '400',
                      flexGrow: 1 
                    }}>
                      {t('nav.master')}
                    </Typography>
                    {masterOpen ? <ExpandLessIcon sx={{ fontSize: 14, color: '#6b7280' }} /> : <ExpandMoreIcon sx={{ fontSize: 14, color: '#6b7280' }} />}
                  </ListItemButton>
                  <Collapse in={masterOpen} timeout="auto" unmountOnExit>
                    <List sx={{ pl: 2, padding: '0 8px' }}>
                      <PermissionGate permission={PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST}>
                        <ListItemButton 
                          onClick={() => { setDrawerOpen(false); navigate("/settings/tracking-event-types"); }}
                          sx={{
                            backgroundColor: isActiveRoute('/settings/tracking-event-types') ? '#f3f4f6' : 'transparent',
                            border: isActiveRoute('/settings/tracking-event-types') ? '1px solid #d1d5db' : '1px solid transparent',
                            borderRadius: '6px',
                            margin: '2px 0',
                            '&:hover': {
                              backgroundColor: isActiveRoute('/settings/tracking-event-types') ? '#f3f4f6' : '#f9fafb',
                              border: isActiveRoute('/settings/tracking-event-types') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                            }
                          }}
                        >
                          <ListItemDecorator>
                            <Box sx={{ 
                              backgroundColor: isActiveRoute('/settings/tracking-event-types') ? '#e5e7eb' : '#f3f4f6', 
                              borderRadius: '4px', 
                              padding: 0.4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <ListAltIcon sx={{ fontSize: 12, color: isActiveRoute('/settings/tracking-event-types') ? '#667eea' : '#6b7280' }} />
                            </Box>
                          </ListItemDecorator>
                          <Typography sx={{ 
                            fontSize: '12px', 
                            color: isActiveRoute('/settings/tracking-event-types') ? '#111827' : '#6b7280',
                            fontWeight: isActiveRoute('/settings/tracking-event-types') ? '500' : '400'
                          }}>
                            {t('nav.trackingEventTypes')}
                          </Typography>
                        </ListItemButton>
                      </PermissionGate>
                      <PermissionGate permission={PERMISSIONS.SHIPPING_COMPANIES_LIST}>
                        <ListItemButton 
                          onClick={() => { setDrawerOpen(false); navigate("/settings/shipping-companies"); }}
                          sx={{
                            backgroundColor: isActiveRoute('/settings/shipping-companies') ? '#f3f4f6' : 'transparent',
                            border: isActiveRoute('/settings/shipping-companies') ? '1px solid #d1d5db' : '1px solid transparent',
                            borderRadius: '6px',
                            margin: '2px 0',
                            '&:hover': {
                              backgroundColor: isActiveRoute('/settings/shipping-companies') ? '#f3f4f6' : '#f9fafb',
                              border: isActiveRoute('/settings/shipping-companies') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                            }
                          }}
                        >
                          <ListItemDecorator>
                            <Box sx={{ 
                              backgroundColor: isActiveRoute('/settings/shipping-companies') ? '#e5e7eb' : '#f3f4f6', 
                              borderRadius: '4px', 
                              padding: 0.4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <LocalShippingIcon sx={{ fontSize: 12, color: isActiveRoute('/settings/shipping-companies') ? '#667eea' : '#6b7280' }} />
                            </Box>
                          </ListItemDecorator>
                          <Typography sx={{ 
                            fontSize: '12px', 
                            color: isActiveRoute('/settings/shipping-companies') ? '#111827' : '#6b7280',
                            fontWeight: isActiveRoute('/settings/shipping-companies') ? '500' : '400'
                          }}>
                            {t('nav.shippingCompanies')}
                          </Typography>
                        </ListItemButton>
                      </PermissionGate>
                      <PermissionGate permission={PERMISSIONS.SHIPPING_WAREHOUSES_LIST}>
                        <ListItemButton 
                          onClick={() => { setDrawerOpen(false); navigate("/settings/shipping-warehouses"); }}
                          sx={{
                            backgroundColor: isActiveRoute('/settings/shipping-warehouses') ? '#f3f4f6' : 'transparent',
                            border: isActiveRoute('/settings/shipping-warehouses') ? '1px solid #d1d5db' : '1px solid transparent',
                            borderRadius: '6px',
                            margin: '2px 0',
                            '&:hover': {
                              backgroundColor: isActiveRoute('/settings/shipping-warehouses') ? '#f3f4f6' : '#f9fafb',
                              border: isActiveRoute('/settings/shipping-warehouses') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                            }
                          }}
                        >
                          <ListItemDecorator>
                            <Box sx={{ 
                              backgroundColor: isActiveRoute('/settings/shipping-warehouses') ? '#e5e7eb' : '#f3f4f6', 
                              borderRadius: '4px', 
                              padding: 0.4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <WarehouseIcon sx={{ fontSize: 12, color: isActiveRoute('/settings/shipping-warehouses') ? '#667eea' : '#6b7280' }} />
                            </Box>
                          </ListItemDecorator>
                          <Typography sx={{ 
                            fontSize: '12px', 
                            color: isActiveRoute('/settings/shipping-warehouses') ? '#111827' : '#6b7280',
                            fontWeight: isActiveRoute('/settings/shipping-warehouses') ? '500' : '400'
                          }}>
                            {t('nav.shippingWarehouses')}
                          </Typography>
                        </ListItemButton>
                      </PermissionGate>
                      <PermissionGate permission={PERMISSIONS.SHIPMENT_TYPES_LIST}>
                        <ListItemButton 
                          onClick={() => { setDrawerOpen(false); navigate("/settings/shipment-types"); }}
                          sx={{
                            backgroundColor: isActiveRoute('/settings/shipment-types') ? '#f3f4f6' : 'transparent',
                            border: isActiveRoute('/settings/shipment-types') ? '1px solid #d1d5db' : '1px solid transparent',
                            borderRadius: '6px',
                            margin: '2px 0',
                            '&:hover': {
                              backgroundColor: isActiveRoute('/settings/shipment-types') ? '#f3f4f6' : '#f9fafb',
                              border: isActiveRoute('/settings/shipment-types') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                            }
                          }}
                        >
                          <ListItemDecorator>
                            <Box sx={{ 
                              backgroundColor: isActiveRoute('/settings/shipment-types') ? '#e5e7eb' : '#f3f4f6', 
                              borderRadius: '4px', 
                              padding: 0.4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <InventoryIcon sx={{ fontSize: 12, color: isActiveRoute('/settings/shipment-types') ? '#667eea' : '#6b7280' }} />
                            </Box>
                          </ListItemDecorator>
                          <Typography sx={{ 
                            fontSize: '12px', 
                            color: isActiveRoute('/settings/shipment-types') ? '#111827' : '#6b7280',
                            fontWeight: isActiveRoute('/settings/shipment-types') ? '500' : '400'
                          }}>
                            {t('nav.shipmentTypes')}
                          </Typography>
                        </ListItemButton>
                      </PermissionGate>
                      <PermissionGate permission={PERMISSIONS.LOCATIONS_LIST}>
                        <ListItemButton 
                          onClick={() => { setDrawerOpen(false); navigate("/settings/locations"); }}
                          sx={{
                            backgroundColor: isActiveRoute('/settings/locations') ? '#f3f4f6' : 'transparent',
                            border: isActiveRoute('/settings/locations') ? '1px solid #d1d5db' : '1px solid transparent',
                            borderRadius: '6px',
                            margin: '2px 0',
                            '&:hover': {
                              backgroundColor: isActiveRoute('/settings/locations') ? '#f3f4f6' : '#f9fafb',
                              border: isActiveRoute('/settings/locations') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                            }
                          }}
                        >
                          <ListItemDecorator>
                            <Box sx={{ 
                              backgroundColor: isActiveRoute('/settings/locations') ? '#e5e7eb' : '#f3f4f6', 
                              borderRadius: '4px', 
                              padding: 0.4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <LocationOnIcon sx={{ fontSize: 12, color: isActiveRoute('/settings/locations') ? '#667eea' : '#6b7280' }} />
                            </Box>
                          </ListItemDecorator>
                          <Typography sx={{ 
                            fontSize: '12px', 
                            color: isActiveRoute('/settings/locations') ? '#111827' : '#6b7280',
                            fontWeight: isActiveRoute('/settings/locations') ? '500' : '400'
                          }}>
                            {t('nav.locations')}
                          </Typography>
                        </ListItemButton>
                      </PermissionGate>
                      <PermissionGate permission={PERMISSIONS.ACCOUNTS_LIST}>
                        <ListItemButton 
                          onClick={() => { setDrawerOpen(false); navigate("/settings/accounts"); }}
                          sx={{
                            backgroundColor: isActiveRoute('/settings/accounts') ? '#f3f4f6' : 'transparent',
                            border: isActiveRoute('/settings/accounts') ? '1px solid #d1d5db' : '1px solid transparent',
                            borderRadius: '6px',
                            margin: '2px 0',
                            '&:hover': {
                              backgroundColor: isActiveRoute('/settings/accounts') ? '#f3f4f6' : '#f9fafb',
                              border: isActiveRoute('/settings/accounts') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                            }
                          }}
                        >
                          <ListItemDecorator>
                            <Box sx={{ 
                              backgroundColor: isActiveRoute('/settings/accounts') ? '#e5e7eb' : '#f3f4f6', 
                              borderRadius: '4px', 
                              padding: 0.4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <AccountBalanceIcon sx={{ fontSize: 12, color: isActiveRoute('/settings/accounts') ? '#667eea' : '#6b7280' }} />
                            </Box>
                          </ListItemDecorator>
                          <Typography sx={{ 
                            fontSize: '12px', 
                            color: isActiveRoute('/settings/accounts') ? '#111827' : '#6b7280',
                            fontWeight: isActiveRoute('/settings/accounts') ? '500' : '400'
                          }}>
                            {t('nav.accounts')}
                          </Typography>
                        </ListItemButton>
                      </PermissionGate>
                      <PermissionGate permission={PERMISSIONS.SETTLEMENT_GROUPS_LIST}>
                        <ListItemButton
                          onClick={() => { setDrawerOpen(false); navigate("/settings/settlementgroups"); }}
                          sx={{
                            backgroundColor: isActiveRoute('/settings/settlementgroups') ? '#f3f4f6' : 'transparent',
                            border: isActiveRoute('/settings/settlementgroups') ? '1px solid #d1d5db' : '1px solid transparent',
                            borderRadius: '6px',
                            margin: '2px 0',
                            '&:hover': {
                              backgroundColor: isActiveRoute('/settings/settlementgroups') ? '#f3f4f6' : '#f9fafb',
                              border: isActiveRoute('/settings/settlementgroups') ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                            }
                          }}
                        >
                          <ListItemDecorator>
                            <Box sx={{
                              backgroundColor: isActiveRoute('/settings/settlementgroups') ? '#e5e7eb' : '#f3f4f6',
                              borderRadius: '4px',
                              padding: 0.4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <AttachMoneyIcon sx={{ fontSize: 12, color: isActiveRoute('/settings/settlementgroups') ? '#667eea' : '#6b7280' }} />
                            </Box>
                          </ListItemDecorator>
                          <Typography sx={{
                            fontSize: '12px',
                            color: isActiveRoute('/settings/settlementgroups') ? '#111827' : '#6b7280',
                            fontWeight: isActiveRoute('/settings/settlementgroups') ? '500' : '400'
                          }}>
                            {t('nav.settlementGroups')}
                          </Typography>
                        </ListItemButton>
                      </PermissionGate>
                    </List>
                  </Collapse>
                  </Box>
                </PermissionGate>
              </List>
            </Collapse>
            </Box>
          </PermissionGate>
          </List>
        </Box>
      </Drawer>
    </>
  );
}
